import { Link as RouterLink } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card, Chip, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Avatar, Typography, Box, IconButton, Tooltip, TextField, InputAdornment,
  useMediaQuery, useTheme, Alert, Skeleton, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack, FormControlLabel, Checkbox, MenuItem,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { AvisoDeLeitura } from "../components/AvisoDeLeitura";
import { useTenant } from "../context/TenantContext";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";

interface User {
  id: string; name: string; email?: string; phone?: string;
  initials: string; role: string; status: string;
}
interface UsersResponse { tenant: string; users: User[]; stale: boolean; error?: string; }
interface Persona { id: string; name: string; description?: string }
interface Acesso { persona_catalog?: Persona[]; areas?: Record<string, boolean> }

// AS QUATRO DO MODELO (Lucas, 04/11/2026). O catalogo tem nove; as outras
// cinco sao duplicata (`owner`), nunca usadas (`collector`, `customer`), vazia
// (`supplier` nao concede NADA) ou do time UpOwl (`internal_admin`).
// Ver o comentario longo em UserEdit.tsx.
const ATRIBUIVEIS = ["seller", "supervisor", "buyer", "executive"];

const AVATAR_COLORS = ["#407899", "#791e94", "#c47c00", "#238f62", "#4a75c9"];

export function Users() {
  const { tenant, isInternal } = useTenant();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const api = useApi();
  const cliente = useQueryClient();
  const [q, setQ] = useState("");
  const [abrir, setAbrir] = useState(false);
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [personas, setPersonas] = useState<string[]>(["seller"]);
  const [rca, setRca] = useState("");
  const [equipes, setEquipes] = useState<string[]>([]);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");

  const usersQ = useQuery({
    queryKey: ["users", tenant],
    queryFn: () => api.call<UsersResponse>("/api/users?limit=500"),
    enabled: !!tenant, staleTime: 60_000,
  });

  const acesso = useQuery({
    queryKey: ["access-personas", tenant],
    queryFn: () => api.call<Acesso>("/api/access"),
    enabled: !!tenant, staleTime: 300_000,
  });
  const podeGerenciar = acesso.data?.areas?.["usuarios.gerenciar"] === true;

  const fechar = () => {
    setAbrir(false); setErro("");
    setNome(""); setTelefone(""); setEmail(""); setPersonas(["seller"]); setRca(""); setEquipes([]);
  };
  const convidar = useMutation({
    mutationFn: () => api.call<{ telefone: string }>("/api/users", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nome, telefone, email, personas, sales_rep_id: rca,
                             sales_team_ids: equipes }),
    }),
    onSuccess: (r) => {
      setAviso(`Usuário criado em ${r.telefone}.`);
      fechar();
      cliente.invalidateQueries({ queryKey: ["users", tenant] });
    },
    onError: (e: any) => setErro(e?.detalhe || "Não consegui criar o usuário agora."),
  });
  const equipesQ = useQuery({
    queryKey: ["equipes", tenant],
    queryFn: () => api.call<{ equipes: { id: string; vendedores: number }[] }>("/api/users/equipes"),
    enabled: !!tenant && podeGerenciar, staleTime: 300_000,
  });
  const ehVendedor = personas.includes("seller");
  const semRca = ehVendedor && !rca.trim();
  const ehSupervisor = personas.includes("supervisor");
  const semEquipe = ehSupervisor && !equipes.length;

  const all = usersQ.data?.users ?? [];
  const items = all.filter(
    (u) => q === "" ||
      u.name.toLowerCase().includes(q.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <>
      <PageHeader
        eyebrow="Gestão"
        title="Usuários"
        subtitle={<>{all.length} usuários · escopo <strong>{isInternal ? tenant + " (admin)" : tenant ?? "?"}</strong></>}
        action={
          <Button variant="contained" startIcon={<PersonAddRoundedIcon />} data-testid="btn-invite"
            disabled={!podeGerenciar} onClick={() => setAbrir(true)}>
            Convidar
          </Button>
        }
      />

      {aviso && <Alert severity="success" onClose={() => setAviso("")}>{aviso}</Alert>}

      {usersQ.data?.stale && <AvisoDeLeitura erro={usersQ.data.error} oQue="a lista de usuários" />}

      <Dialog open={abrir} onClose={fechar} fullWidth maxWidth="sm">
        <DialogTitle>Convidar usuário em {tenant}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {erro && <Alert severity="error">{erro}</Alert>}
            <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} required fullWidth />
            <TextField label="Telefone" value={telefone} onChange={(e) => setTelefone(e.target.value)}
              required fullWidth placeholder="5585988887777"
              helperText="55 + DDD + número. Nos DDD 85 e 88 o nono dígito é removido automaticamente."
              InputProps={{ sx: { fontFamily: "monospace" } }} />
            <TextField label="E-mail (opcional)" value={email} onChange={(e) => setEmail(e.target.value)}
              fullWidth helperText="Só quem vai usar a plataforma precisa. Vendedor entra pelo WhatsApp." />
            <Box>
              <Typography variant="caption" color="text.secondary">Permissões</Typography>
              <Box>
                {(acesso.data?.persona_catalog ?? [])
                  .filter((p) => ATRIBUIVEIS.includes(p.id))
                  .map((p) => (
                    <Tooltip key={p.id} title={p.description || ""} placement="right">
                      <FormControlLabel
                        control={
                          <Checkbox checked={personas.includes(p.id)}
                            onChange={(e) => setPersonas((atual) =>
                              e.target.checked ? [...atual, p.id] : atual.filter((x) => x !== p.id))} />
                        }
                        label={p.name}
                      />
                    </Tooltip>
                  ))}
              </Box>
            </Box>
            {ehSupervisor && (
              <TextField select SelectProps={{ multiple: true }} label="Equipes" value={equipes}
                onChange={(e) => setEquipes(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : (e.target.value as unknown as string[]))}
                required error={semEquipe}
                helperText={semEquipe
                  ? "Supervisor sem equipe não vê nenhum dado."
                  : "Ele vê os vendedores destas equipes, e só deles."}>
                {(equipesQ.data?.equipes ?? []).map((eq) => (
                  <MenuItem key={eq.id} value={eq.id}>
                    Equipe {eq.id} · {eq.vendedores} vendedor{eq.vendedores === 1 ? "" : "es"}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {ehVendedor && (
              <TextField label="RCA (código do vendedor no ERP)" value={rca}
                onChange={(e) => setRca(e.target.value)} required error={semRca}
                helperText={semRca ? "Vendedor sem RCA não vê nenhum dado." : "Define de quem são os números que esta pessoa vê."}
                InputProps={{ sx: { fontFamily: "monospace" } }} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fechar} disabled={convidar.isPending}>Cancelar</Button>
          <Button variant="contained" onClick={() => convidar.mutate()}
            disabled={convidar.isPending || !nome.trim() || !telefone.trim()
              || !personas.length || semRca || semEquipe}>
            Criar usuário
          </Button>
        </DialogActions>
      </Dialog>

      <TextField
        placeholder="Buscar por nome ou email..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 380 }}
        inputProps={{ "data-testid": "user-search" }}
      />

      <Card variant="outlined" sx={{ overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Nome</TableCell>
              {!isSm && <TableCell>Email</TableCell>}
              {!isSm && <TableCell>Telefone</TableCell>}
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {usersQ.isLoading && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton variant="circular" width={28} height={28} /></TableCell>
                <TableCell><Skeleton width={140} /></TableCell>
                {!isSm && <TableCell><Skeleton width={180} /></TableCell>}
                {!isSm && <TableCell><Skeleton width={120} /></TableCell>}
                <TableCell><Skeleton width={60} /></TableCell>
                <TableCell><Skeleton width={60} /></TableCell>
                <TableCell />
              </TableRow>
            ))}
            {!usersQ.isLoading && items.map((u, i) => (
              <TableRow key={u.id || u.name} hover>
                <TableCell>
                  <Avatar sx={{ width: 32, height: 32, fontSize: 12, bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{u.initials}</Avatar>
                </TableCell>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{u.name}</Typography></TableCell>
                {!isSm && <TableCell><Typography variant="body2" color="text.secondary">{u.email || "—"}</Typography></TableCell>}
                {!isSm && <TableCell><Typography variant="body2" sx={{ fontFamily: "monospace", color: "text.secondary" }}>{u.phone || "—"}</Typography></TableCell>}
                <TableCell><Chip label={u.role} size="small" variant="outlined" /></TableCell>
                <TableCell><Chip label={u.status === "active" ? "Ativo" : u.status} size="small" color={u.status === "active" ? "success" : "default"} /></TableCell>
                <TableCell align="right">
                  {u.id && (
                    <Tooltip title="Ver detalhe"><IconButton size="small" component={RouterLink} to={`/usuarios/${u.id}`}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!usersQ.isLoading && items.length === 0 && (
              <TableRow><TableCell colSpan={7}><Box sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>Nenhum usuário encontrado.</Box></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
